// Package Block
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { put, takeLatest } from "redux-saga/effects";
import jwt_decode from "jwt-decode";

// Auth Crud Block
import { getUserByToken, updateProfilePictureRequest } from "./authCrud";

export const actionTypes = {
  Login: "[Login] Action",
  RefreshToken: "[Refresh] Action",
  Logout: "[Logout] Action",
  Register: "[Register] Action",
  UserRequested: "[Request User] Action",
  UserLoaded: "[Load User] Auth API",
  UpdateUser: "[Update User] Update API",
  UpdateLoginPin: "[Update Login Pin] Update API",
  UpdateProfilePicture: "[Update Profile Picture] Action",
  ProfilePictureUpdated: "[Profile Picture Updated] Action",
  Uploading: "[Uploading] Action",
  Catch: "[Catch Error] Action",
  CyclrToken: "[CyclrToken] Action",
  MarketplaceUrl: "[MarketplaceUrl] Action",
};

const initialAuthState = {
  user: undefined,
  authToken: undefined,
  refreshToken: undefined,
  cyclrToken: undefined,
  marketplaceUrl: undefined,
  uploading: false,
  pilotUrl: null,
  admin: {},
  message: {
    success: null,
    error: null,
  },
};

export const reducer = persistReducer(
  {
    storage,
    key: "demo1-auth",
    whitelist: ["user", "authToken", "refreshToken", "admin", "profile", "cyclrToken", "marketplaceUrl"],
  },
  (state = initialAuthState, action) => {
    switch (action.type) {
      case actionTypes.Login: {
        const { authToken, refreshToken, pilotUrl } = action.payload;
        return {
          ...state,
          authToken,
          refreshToken,
          pilotUrl,
          // 🛑 Don't reset user immediately
          user: state.user ?? undefined,  // ✅ Keep user data instead of resetting
          admin: jwt_decode(authToken).sub?.profile
        };
      }


      case actionTypes.Register: {
        const { authToken } = action.payload;

        return { ...state, authToken, user: undefined };
      }

      case actionTypes.Logout: {
        // TODO: Change this code. Actions in reducer aren't allowed.
        return initialAuthState;
      }

      case actionTypes.UserLoaded: {
        const { user } = action.payload;
        return { ...state, user };
      }

      case actionTypes.ProfilePictureUpdated: {
        const { entity, message } = action.payload;
        return { ...state, user: { ...state.user, ...entity }, message: { success: message, error: null } };
      }
      case actionTypes.Uploading: {
        const { uploading } = action.payload;
        return { ...state, uploading };
      }
      case actionTypes.UpdateUser: {
        const { user } = action.payload;
        return { ...state, user: { ...state.user, ...user } };
      }
      case actionTypes.Catch: {
        const error =
          action.payload?.error?.response && action.payload?.error?.response.data
            ? [action.payload?.error?.response.data.message, ...action.payload?.error?.response.data.errors]
            : action.payload?.error?.message;
        return {
          ...state,
          uploading: false,
          message: {
            success: null,
            error: error,
          },
        };
      }
      case "mutateToken": {
        return {
          ...state,
          authToken:
            "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpYXQiOjE2NTEwOTYzMjQsIm5iZiI6MTY1MTA5NjMyNCwianRpIjoiMzM5NWM0OWUtNzAzZS00OTgxLTg3NDgtMGI2OWJhY2I3ZTJiIiwiZXhwIjoxNjUxMzU1NTI0LCJpZGVudGl0eSI6eyJ1c2VyX2lkIjoxLCJ0eXBlIjoxfSwiZnJlc2giOmZhbHNlLCJ0eXBlIjoiYWNjZXNzIn0.XK57j7IThCHkFm6y1Xlq4IrewMqListVFKEClhB299Y",
        };
      }
      case actionTypes.RefreshToken: {
        const { authToken, refreshToken } = action.payload;
        return { ...state, authToken, refreshToken };
      }
      case actionTypes.CyclrToken: {
        const { cyclrToken } = action.payload;
        return { ...state, cyclrToken };
      }
      case actionTypes.MarketplaceUrl: {
        const { marketplaceUrl } = action.payload;
        return { ...state, marketplaceUrl };
      }
      case actionTypes.UpdateLoginPin: {
        const { login_pin } = action.payload;
        return {
          ...state,
          user: {
            ...state.user,
            entity: {
              ...state.user?.entity,
              ...login_pin,
            },
          },
        };
      }
      default:
        return state;
    }
  }
);

export const actions = {
  login: (authToken, refreshToken, pilotUrl) => ({ type: actionTypes.Login, payload: { authToken, refreshToken, pilotUrl } }),
  register: (authToken) => ({
    type: actionTypes.Register,
    payload: { authToken },
  }),
  logout: () => ({ type: actionTypes.Logout }),
  requestUser: (user) => ({
    type: actionTypes.UserRequested,
    payload: { user },
  }),
  updateUser: (user) => {
    return {
      type: actionTypes.UpdateUser,
      payload: { user },
    };
  },
  updateProfilePicture: (payload) => {
    return {
      type: actionTypes.UpdateProfilePicture,
      payload: payload,
    };
  },
  updateLoginPin: (login_pin) => ({
    type: actionTypes.UpdateLoginPin,
    payload: { login_pin },
  }),
  
  profilePictureUpdated: (entity) => ({ type: actionTypes.ProfilePictureUpdated, payload: entity }),
  fulfillUser: (user) => ({ type: actionTypes.UserLoaded, payload: { user } }),
  uploading: (uploading) => ({ type: actionTypes.Uploading, payload: { uploading } }),
  refresh_token: (authToken, refreshToken) => ({
    type: actionTypes.RefreshToken,
    payload: { authToken, refreshToken },
  }),
  cyclrToken: (cyclrToken) => ({ type: actionTypes.CyclrToken, payload: { cyclrToken } }),
  marketplaceUrl: (marketplaceUrl) => ({ type: actionTypes.MarketplaceUrl, payload: { marketplaceUrl } }),
};

export function* authSaga() {
  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.Register, function* registerSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.UserRequested, function* userRequested(action) {
    const { data: entity } = yield getUserByToken();
    yield put(actions.fulfillUser(entity));
  });
  yield takeLatest(actionTypes.UpdateProfilePicture, function* updateProfilePicture(action) {
    yield put(actions.uploading(true));
    const { data: entity } = yield updateProfilePictureRequest(action.payload);
  });
  yield takeLatest(actionTypes.UpdateUser, function* updatedUserSaga() {
    yield put(actions.requestUser());
  });
}
